import {
  DB_CODE_35M_011,
  DB_CODE_35M_012,
  DB_CODE_35M_015,
  X_SERIES,
  XMC3,
} from './constants/vehicle-constants'

const CUTOFF_DATE_35M = '2025-09-29T00:00:00Z'

/**
 * Determines if a 43N vehicle should use the 42N pinout layout.
 * - 35M-012 always aligns with 42N (existing behavior)
 * - 35M-011 or 35M-015 aligns with 42N only when built on/after 09/29/2025
 * @param {string} db35mCode - The vehicle's 35M DB code
 * @param {string} buildDate - The vehicle build date (ISO string)
 * @returns {boolean} - True if 43N should use 42N pinout layout
 */
export const is43NAlignedWith42N = (db35mCode, buildDate) => {
  if (db35mCode === DB_CODE_35M_012) return true

  if (db35mCode === DB_CODE_35M_011 || db35mCode === DB_CODE_35M_015) {
    if (!buildDate) return false
    return new Date(buildDate) >= new Date(CUTOFF_DATE_35M)
  }

  return false
}

/**
 * Check if a vehicle/module/sales code combination has DB code restrictions
 * @param {string} vehicleType - The vehicle type
 * @param {string} module - The module name
 * @param {string} salesCode - The sales code
 * @returns {boolean} - True if has DB code restrictions
 */
export const hasDbCodeRestrictions = (vehicleType, module, salesCode) =>
  vehicleType === X_SERIES &&
  module === XMC3 &&
  (salesCode === DB_CODE_35M_011 || salesCode === DB_CODE_35M_015)

/**
 * Get connector restriction information for displaying before/after date variations
 * @param {Object} params - Parameters object
 * @param {Array} params.interfacePins - Array of interface pin data
 * @param {string} params.vehicleType - Current vehicle type
 * @param {string} params.module - Current module name
 * @param {string} params.salesCode - Current sales code
 * @param {string} params.connector - Current connector location string
 * @returns {Object} - { hasSameLocation: boolean, connectorBefore: string|null, isPinVpdmOutputBefore: boolean, messageBefore: string|null }
 */
export const getConnectorRestrictions = ({
  interfacePins,
  vehicleType,
  module,
  salesCode,
  connector,
}) => {
  // Check if this combination has restrictions
  if (!hasDbCodeRestrictions(vehicleType, module, salesCode)) {
    return {
      hasSameLocation: false,
      connectorBefore: null,
      isPinVpdmOutputBefore: false,
      messageBefore: null,
    }
  }

  // Find the other vehicle entry (non-X-Series) for comparison
  const otherVehicleEntry = interfacePins.find(
    (item) => item.module === XMC3 && item.vehicleSubType !== vehicleType
  )

  // No other vehicle entry found
  if (!otherVehicleEntry) {
    return {
      hasSameLocation: false,
      connectorBefore: null,
      isPinVpdmOutputBefore: false,
      messageBefore: null,
    }
  }

  // Get pin data from the other vehicle
  const otherPinData = otherVehicleEntry.pins[salesCode]

  // Build the connector string from the other vehicle's data
  let connectorBefore = null
  if (otherPinData?.extensionConnector && otherPinData?.extensionPin) {
    connectorBefore = [
      otherPinData.extensionConnector,
      otherPinData.extensionPin,
    ].join(', ')
  }

  // Check if both connectors are the same (no date variation needed)
  const hasSameLocation = connector === connectorBefore

  return {
    hasSameLocation,
    connectorBefore,
    isPinVpdmOutputBefore: otherPinData?.isVpdmOutput || false,
    messageBefore: otherPinData?.message || null,
  }
}
